import React, { FC, Fragment, useMemo } from 'react';
import { NetworkStatus, useQuery } from '@apollo/client';
import { Box, Checkbox, Spinner, SpinnerSize, Button, ButtonSize, Text, Icon } from '@bechtel/gds';
import {
  CommodityMilestone,
  CommodityFilterInput,
  CommodityList as Commodities,
  PagingInput,
  WorkPackage,
  CommodityFilterByTerm,
  FilterOperator,
} from '@bechtel/types';
import { GET_COMMODITIES } from '@bechtel/client-gql';
import {
  TextWithEllipsis,
  ListSelectionStyled,
  useGroupedCommodities,
  commodityPageSize,
} from '@bechtel/client-common';
import { FieldArray } from 'formik';
import { findIndex, get, map, some } from 'lodash';
import { CommodityListStyled } from './styled';
import { Trans } from '@lingui/macro';
import { Commodity } from '~/types';

interface MultiCommodityListProps {
  workPackageId: WorkPackage['id'];
  selectedCommodityTypeId: string;
}

export const MultiCommodityList: FC<MultiCommodityListProps> = ({ workPackageId, selectedCommodityTypeId }) => {
  const { loading, error, data, refetch, networkStatus, fetchMore } = useQuery<
    { commodityList: Commodities },
    {
      input: CommodityFilterInput;
      paging: PagingInput;
    }
  >(GET_COMMODITIES, {
    variables: {
      input: {
        complexFilter: {
          and: [
            { term: CommodityFilterByTerm.WORK_PACKAGE_ID, value: workPackageId, operator: FilterOperator.EQUALS },
            {
              term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
              value: selectedCommodityTypeId,
              operator: FilterOperator.EQUALS,
            },
            { term: CommodityFilterByTerm.EXCLUDE_CONNECTED_COMMODITY, value: 'true', operator: FilterOperator.EQUALS },
          ],
        },
      },
      paging: { cursor: '1', limit: commodityPageSize },
    },
    notifyOnNetworkStatusChange: true,
  });
  const commodities = useMemo(() => get(data, 'commodities.items', []), [data]);

  const groupedCommodities = useGroupedCommodities(commodities);

  const filteredCommodities = groupedCommodities ? groupedCommodities[selectedCommodityTypeId] : [];
  const cursor = get(data, 'commodities.cursor');
  const total = get(data, 'commodities.total');

  const handleScroll = (e) => {
    if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight && commodities?.length < total) {
      fetchMore({
        variables: {
          paging: { cursor: cursor, limit: commodityPageSize },
        },
      });
    }
  };

  if (error) {
    return (
      <Box css="height: 100%;" justifyContent={{ sm: 'center' }} alignItems={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
        <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
          <Text>
            <Trans>There was a problem loading the commodities.</Trans>
          </Text>
          <Text>
            <Trans>Please click the button below to try again.</Trans>
          </Text>
        </Box>
        <Box>
          <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }
  return (
    <CommodityListStyled
      onScroll={(e) => {
        handleScroll(e);
      }}
    >
      <FieldArray name="commodityList">
        {({ push, remove, form }) => {
          const commodityList: Commodity[] = form.values.commodityList;

          return (
            <Fragment>
              {map(filteredCommodities, (commodity: Commodity) => {
                const selected = some(commodityList, { id: commodity.id });

                const onClick = () => {
                  if (selected) {
                    const index = findIndex(commodityList, { id: commodity.id });

                    remove(index);
                  } else {
                    const element = {
                      id: commodity.id,
                      tag: commodity.tag,
                      type: commodity.type,
                      milestones: map(commodity.milestones, (milestone: CommodityMilestone) => {
                        return { id: milestone.id, name: milestone.name };
                      }),
                    };

                    push(element);
                  }
                };

                return (
                  <ListSelectionStyled key={commodity.id} onClick={onClick}>
                    <Box mr={{ sm: '500' }}>
                      <Checkbox checked={selected} readOnly />
                    </Box>
                    <TextWithEllipsis family="graphik" size="400" color="grey.1000">
                      {commodity.tag}
                    </TextWithEllipsis>
                  </ListSelectionStyled>
                );
              })}
            </Fragment>
          );
        }}
      </FieldArray>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box
          css="height: 100%;"
          data-testid="loading"
          justifyContent={{ sm: 'center' }}
          alignItems={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
    </CommodityListStyled>
  );
};
export default MultiCommodityList;
